import { Credits } from './credits';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { api, errorLine } from '../../api/client';
import type { AdminOrg } from '../../api/types';
import { Tabs, TabPanel, type TabSpec } from '../../components/ui/tabs';
import { ORG_TAB_LABELS } from '../../lib/copy';
import { formatDay } from '../../lib/format';
import { replaceOrg } from '../../lib/org-list';
import { ConnectorPanel } from './connector';
import { DeleteOrgModal } from './delete-org-modal';
import { Members } from './members';
import { ResearchSettingsForm } from './research';
import { Spend } from './spend';

/**
 * **One organization, on an address of its own** — the page, its tabs, and where
 * each of them lives.
 *
 * The panels are one file each beside this one (`members.tsx`, `research.tsx`,
 * `spend.tsx`, `credits.tsx`, `connector.tsx`); what is here is the frame around
 * them: the read that finds the org row, the head, and the narrowing that turns
 * a `:tab` parameter into one of the panels.
 */

/**
 * **Where an organization lives** — one place it is spelled, so a link, a
 * redirect and a navigate after a create cannot drift apart.
 *
 * `/organizations` and not `/orgs`, which is the address this page would
 * otherwise have taken. `/orgs/*` is an **API namespace** on the server and is
 * excluded from the SPA history fallback on *both* hosts (`server/src/common/
 * spa.ts`), so a deep link to `/orgs/<id>/members` — or a reload of one — would
 * be answered with a JSON 404 rather than this bundle, in production only. The
 * office dashboard hit the same wall and answered it the same way: its own org
 * record is `/organizations/:id`, and `spa.spec.ts` pins that `/organizations`
 * is not an API path precisely so it stays that way.
 */
export const orgPath = (orgId: string, tab: OrgTab = 'members'): string =>
  `/organizations/${orgId}/${tab}`;

/** The tabs, and the only `:tab` values the route accepts. */
export const ORG_TABS = ['members', 'research', 'spend', 'credits', 'connector'] as const;

export type OrgTab = (typeof ORG_TABS)[number];

const isOrgTab = (value: string | undefined): value is OrgTab =>
  ORG_TABS.includes((value ?? '') as OrgTab);

/**
 * **One organization, on an address of its own** (owner ruling, 2026-09-10:
 * "selecting an org navigates to a dedicated org page").
 *
 * The org row comes out of `GET /admin/orgs`, because that is the only read this
 * app has that answers with one — there is no `GET /admin/orgs/:id`. So the page
 * reads the list, finds its row, and holds it: one request for a screen's worth
 * of settings, which is what the list was already doing when the detail hung
 * below it.
 *
 * **Holding the row here is what makes the writes settle.** Both settings forms
 * open from this row and hand back the row the server settled on; `replaceOrg`
 * puts it where the stale one was. Without that the forms' `saved` baselines
 * would be seeded from the read on the next tab switch, and a cap that was just
 * written would go missing from the field that wrote it. It was the list page's
 * job while the panels hung under the list; it is this page's now.
 *
 * A `key` on the whole page (see {@link OrgRoute}) rather than on the panels:
 * moving between organizations must remount every form on it, and a form that
 * kept a half-typed limit across that would be offering to write one org's
 * number onto another.
 */
export function OrgPage({ orgId, tab }: { orgId: string; tab: OrgTab }) {
  const [orgs, setOrgs] = useState<AdminOrg[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let live = true;
    api
      .listOrgs()
      .then((answer) => {
        if (!live) return;
        setOrgs(answer.orgs);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (live) setError(errorLine(caught));
      });
    return () => {
      live = false;
    };
  }, [orgId]);

  const onSaved = useCallback((saved: AdminOrg) => {
    setOrgs((rows) => replaceOrg(rows, saved));
  }, []);

  const org = orgs?.find((row) => row.id === orgId) ?? null;
  return (
    <>
      <OrgPageView
        org={org}
        tab={tab}
        loading={orgs === null && error === null}
        missing={orgs !== null && !orgs.some((row) => row.id === orgId)}
        error={error}
        onSaved={onSaved}
        onDelete={() => setDeleting(true)}
      />
      {org && deleting ? (
        <DeleteOrgModal
          org={org}
          onClose={() => setDeleting(false)}
          onDeleted={() => navigate('/', { replace: true })}
        />
      ) : null}
    </>
  );
}

/**
 * The org page with the read taken out of it — the split `MembersTable` and
 * `OrgsTable` are: a component that holds no state and fires no requests, so the
 * copy sweep can render every state it has from a fixture. A page driven only by
 * effects reaches none of them under `renderToStaticMarkup`, and the head of a
 * screen is exactly where a stale word survives longest.
 */
export function OrgPageView({
  org,
  tab,
  loading,
  missing,
  error,
  onSaved,
  onDelete,
}: {
  org: AdminOrg | null;
  tab: OrgTab;
  loading: boolean;
  /** The list came back and holds no such id. */
  missing: boolean;
  error: string | null;
  onSaved?: (org: AdminOrg) => void;
  /** Opens the delete dialog; the page holds it, so the view stays stateless. */
  onDelete?: () => void;
}) {
  return (
    <>
      <div className="breadcrumb">
        <Link className="link" to="/">
          Organizations
        </Link>
        {org ? ` / ${org.name}` : null}
      </div>

      <div className="head">
        <div className="titles">
          <div className="title">{org?.name ?? 'Organization'}</div>
          <div className="subline">
            {loading ? 'Loading.' : null}
            {org ? (
              <>
                {/* The vertical is the raw key, exactly as the list column
                    prints it: this app carries no label table of its own, and
                    the labels live behind a read this page does not make. */}
                {org.slug} · <span className="quiet-key">{org.vertical}</span> · created{' '}
                {formatDay(org.created_at)}
              </>
            ) : null}
          </div>
        </div>
        {org && onDelete ? (
          <div className="actions-right">
            <button type="button" className="btn act" onClick={onDelete}>
              Delete organization
            </button>
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="flag row">
          <div className="label">Error</div>
          <div className="detail">{error}</div>
        </div>
      ) : null}

      {missing ? (
        <div className="flag row">
          <div className="label">Not found</div>
          <div className="detail">
            No organization on this server has that id.{' '}
            <Link className="link" to="/">
              Organizations
            </Link>
          </div>
        </div>
      ) : null}

      {org ? (
        <>
          <Tabs tabs={orgTabs(org.id)} active={tab} />
          <TabPanel>
            {tab === 'credits' ? <Credits orgId={org.id} /> : null}
            {tab === 'members' ? <Members org={org} /> : null}
            {tab === 'research' ? (
              <div className="section">
                <ResearchSettingsForm org={org} onSaved={onSaved} />
              </div>
            ) : null}
            {tab === 'spend' ? (
              <div className="section">
                <Spend org={org} onSaved={onSaved} />
              </div>
            ) : null}
            {/* Org-independent: one Auth0 client serves every organization, so
                this tab says the same three things whichever org is open. It is
                here because this is where an operator is standing when somebody
                asks how to connect. */}
            {tab === 'connector' ? <ConnectorPanel /> : null}
          </TabPanel>
        </>
      ) : null}
    </>
  );
}

export const orgTabs = (orgId: string): TabSpec[] =>
  ORG_TABS.map((key) => ({ key, label: ORG_TAB_LABELS[key], to: orgPath(orgId, key) }));

/**
 * The route around {@link OrgPage}: it narrows `:tab` to the five this page has,
 * and sends anything else — a typo, a link from a version of this app that had a
 * sixth — to Members rather than showing an empty panel.
 *
 * The `key` is the whole reason this is a component rather than `useParams`
 * inside the page: a route element is *not* remounted when only its params
 * change, so without it walking from one org to another would keep every form's
 * state and every in-flight read.
 */
export function OrgRoute() {
  const { orgId, tab } = useParams();
  if (!orgId) return <Navigate to="/" replace />;
  if (!isOrgTab(tab)) return <Navigate to={orgPath(orgId)} replace />;
  return <OrgPage key={orgId} orgId={orgId} tab={tab} />;
}
